import React, { useState, useEffect } from "react";
import {
  getAllPackages,
  createPackage,
  updatePackage,
  deletePackage,
  togglePackageStatus,
  getDefaultPackageTemplate,
  PACKAGE_TYPES,
  PACKAGE_STATUS,
} from "../services/packageService";
import LoadingSpinner from "./LoadingSpinner";
import ActivatePackageByEmailModal from "./ActivatePackageByEmailModal";

// Predefined package features for employers/speakers
const PREDEFINED_EMPLOYER_FEATURES = [
  "Unlimited Job Applications",
  "10 Days Job Validity",
  "15 Days Job Validity",
  "30 Days Job Validity",
  "60 Days Job Posting",
  "1 Job Posting",
  "2 Job Posting",
  "3 Job Posting",
  "10 Job Postings",
  "Premium Listing Position",
  "5 Pre Screened Leads",
  "20 Database Candidates",
];

const PackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showActivateEmailModal, setShowActivateEmailModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState(
    getDefaultPackageTemplate(PACKAGE_TYPES.JOB_SEEKER)
  );
  const [submitting, setSubmitting] = useState(false);
  const [newFeature, setNewFeature] = useState("");
  const [filterType, setFilterType] = useState("all"); // 'all', 'job_seeker', 'employer'
  const [selectedPredefinedFeatures, setSelectedPredefinedFeatures] = useState(
    []
  );

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    const result = await getAllPackages();
    if (result.success) {
      setPackages(result.packages);
    }
    setLoading(false);
  };

  const handleCreateNew = (type) => {
    setEditingPackage(null);
    setFormData(getDefaultPackageTemplate(type));
    setSelectedPredefinedFeatures([]);
    setShowForm(true);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      ...pkg,
    });
    // Set selected predefined features based on existing features
    const predefinedInPackage = (pkg.features || []).filter((f) =>
      PREDEFINED_EMPLOYER_FEATURES.includes(f)
    );
    setSelectedPredefinedFeatures(predefinedInPackage);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let result;
      if (editingPackage) {
        result = await updatePackage(editingPackage.id, formData);
      } else {
        result = await createPackage(formData);
      }

      if (result.success) {
        setShowForm(false);
        setEditingPackage(null);
        fetchPackages();
        alert(
          `Package ${
            editingPackage ? "updated" : "created"
          } successfully! Notifications have been sent to relevant users.`
        );
      } else {
        alert("Error: " + result.error);
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (window.confirm("Are you sure you want to delete this package?")) {
      const result = await deletePackage(packageId);
      if (result.success) {
        fetchPackages();
      } else {
        alert("Error deleting package: " + result.error);
      }
    }
  };

  const handleToggleStatus = async (pkg) => {
    const result = await togglePackageStatus(pkg.id, pkg.status);
    if (result.success) {
      fetchPackages();
    } else {
      alert("Error updating status: " + result.error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // When type changes to database, ensure durationDays and maxContacts are set
      if (field === "type" && value === "database") {
        if (!updated.durationDays) updated.durationDays = 30;
        if (!updated.maxContacts) updated.maxContacts = 10;
      }

      return updated;
    });
  };

  const togglePredefinedFeature = (feature) => {
    setSelectedPredefinedFeatures((prev) => {
      if (prev.includes(feature)) {
        // Remove from selected
        return prev.filter((f) => f !== feature);
      } else {
        // Add to selected
        return [...prev, feature];
      }
    });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      // Check if it's not already in predefined features
      const customFeatures = (formData.features || []).filter(
        (f) => !PREDEFINED_EMPLOYER_FEATURES.includes(f)
      );
      setFormData((prev) => ({
        ...prev,
        features: [
          ...selectedPredefinedFeatures,
          ...customFeatures,
          newFeature.trim(),
        ],
      }));
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    const featureToRemove = formData.features[index];
    // If it's a predefined feature, remove from selected list
    if (PREDEFINED_EMPLOYER_FEATURES.includes(featureToRemove)) {
      setSelectedPredefinedFeatures((prev) =>
        prev.filter((f) => f !== featureToRemove)
      );
    }
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // Sync selected predefined features with formData
  useEffect(() => {
    if (showForm) {
      const customFeatures = (formData.features || []).filter(
        (f) => !PREDEFINED_EMPLOYER_FEATURES.includes(f)
      );
      setFormData((prev) => ({
        ...prev,
        features: [...selectedPredefinedFeatures, ...customFeatures],
      }));
    }
  }, [selectedPredefinedFeatures]);

  // Filter packages based on selected type
  const filteredPackages = packages.filter((pkg) => {
    if (filterType === "all") return true;
    return pkg.type === filterType;
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Package Management</h1>
        <p className="text-sm text-gray-500">
          Create and manage packages for job seekers and employers
        </p>
      </div>

      {/* Filter Dropdown */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <label className="text-sm font-medium text-gray-700">
            Filter by Type:
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white w-full sm:w-auto"
          >
            <option value="all">All Packages ({packages.length})</option>
            <option value={PACKAGE_TYPES.JOB_SEEKER}>
              Employee Packages (
              {
                packages.filter((p) => p.type === PACKAGE_TYPES.JOB_SEEKER)
                  .length
              }
              )
            </option>
            <option value={PACKAGE_TYPES.EMPLOYER}>
              Speaker Packages (
              {packages.filter((p) => p.type === PACKAGE_TYPES.EMPLOYER).length}
              )
            </option>
            <option value="database">
              Database Packages (
              {packages.filter((p) => p.type === "database").length})
            </option>
          </select>
        </div>

        <div className="text-sm text-gray-500 self-start sm:self-center">
          Showing {filteredPackages.length} of {packages.length} packages
        </div>
      </div>

      {/* Create Package Buttons & Email Activation */}
      <div className="mb-6 flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
        <button
          onClick={() => setShowActivateEmailModal(true)}
          className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all font-bold shadow-md w-full sm:w-auto text-center flex items-center justify-center gap-2"
        >
          ⚡ Activate Package by Email
        </button>
        <button
          onClick={() => handleCreateNew(PACKAGE_TYPES.JOB_SEEKER)}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium w-full sm:w-auto text-center"
        >
          + Create Employee Package
        </button>
        <button
          onClick={() => handleCreateNew(PACKAGE_TYPES.EMPLOYER)}
          className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium w-full sm:w-auto text-center"
        >
          + Create Speaker Package
        </button>
        <button
          onClick={() => handleCreateNew("database")}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium w-full sm:w-auto text-center"
        >
          + Create Database Package
        </button>
      </div>

      {/* Activate Package By Email Modal */}
      <ActivatePackageByEmailModal
        isOpen={showActivateEmailModal}
        onClose={() => setShowActivateEmailModal(false)}
      />

      {/* Package Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <h2 className="text-xl font-bold mb-4">
              {editingPackage ? "Edit Package" : "Create New Package"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Basic Plan, Premium Plan"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Type *
                  </label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => handleInputChange("type", e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={PACKAGE_TYPES.JOB_SEEKER}>
                      Employee (Job Seeker)
                    </option>
                    <option value={PACKAGE_TYPES.EMPLOYER}>
                      Speaker (Employer)
                    </option>
                    <option value="database">Database Access</option>
                  </select>
                </div>
              </div>

              {/* Database Package Specific Fields */}
              {formData.type === "database" && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold text-green-900">
                    Database Access Settings
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Contacts to Unlock *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.maxContacts || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "maxContacts",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 50, 100, 200"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of job seeker contacts that can be unlocked with
                      this package
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validity (Days) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.durationDays || 30}
                      onChange={(e) =>
                        handleInputChange(
                          "durationDays",
                          parseInt(e.target.value) || 30
                        )
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 30, 60, 90"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How long the package remains active after purchase
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe what this package offers..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) =>
                      handleInputChange(
                        "price",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Only show Duration field for non-database packages */}
                {formData.type !== "database" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (days) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.duration}
                      onChange={(e) =>
                        handleInputChange(
                          "duration",
                          parseInt(e.target.value) || 30
                        )
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Package-specific fields */}
              {formData.type === PACKAGE_TYPES.JOB_SEEKER && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Applications
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.maxApplications || 0}
                      onChange={(e) =>
                        handleInputChange(
                          "maxApplications",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {formData.type === PACKAGE_TYPES.EMPLOYER && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Job Postings
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.maxJobPostings || 0}
                      onChange={(e) =>
                        handleInputChange(
                          "maxJobPostings",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Featured Jobs
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.featuredJobs || 0}
                      onChange={(e) =>
                        handleInputChange(
                          "featuredJobs",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="candidateSearch"
                      checked={formData.candidateSearch || false}
                      onChange={(e) =>
                        handleInputChange("candidateSearch", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label
                      htmlFor="candidateSearch"
                      className="text-sm font-medium text-gray-700"
                    >
                      Candidate Search
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="analyticsAccess"
                      checked={formData.analyticsAccess || false}
                      onChange={(e) =>
                        handleInputChange("analyticsAccess", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label
                      htmlFor="analyticsAccess"
                      className="text-sm font-medium text-gray-700"
                    >
                      Analytics Access
                    </label>
                  </div>
                </div>
              )}

              {/* Features Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Package Features (Points)
                </label>

                {/* Add Custom Feature */}
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">
                    Add Custom Feature:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Add a custom feature (e.g., AI-optimized resume builder)"
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) =>
                        e.key === "Enter" && (e.preventDefault(), addFeature())
                      }
                    />
                    <button
                      type="button"
                      onClick={addFeature}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Features List - Predefined and Custom */}
                {formData.type === PACKAGE_TYPES.EMPLOYER && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Selected Features Section */}
                    {formData.features && formData.features.length > 0 && (
                      <div className="bg-green-50 border-b border-gray-200">
                        <div className="px-4 py-2 bg-green-100 border-b border-green-200">
                          <p className="text-sm font-semibold text-green-800">
                            ✓ Included Features ({formData.features.length})
                          </p>
                        </div>
                        <div className="max-h-60 overflow-y-auto p-2">
                          {formData.features.map((feature, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-white p-3 rounded-lg mb-2 hover:bg-green-50 transition-colors"
                            >
                              <div className="flex items-center flex-1">
                                <svg
                                  className="w-4 h-4 text-green-600 mr-2 flex-shrink-0"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span className="text-sm text-gray-800">
                                  {feature}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFeature(index)}
                                className="text-red-600 hover:text-red-800 transition-colors ml-2"
                                title="Remove feature"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Features Section - Grid with Checkboxes */}
                    <div className="bg-gray-50">
                      <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-700">
                          Available Features (
                          {PREDEFINED_EMPLOYER_FEATURES.length})
                        </p>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {PREDEFINED_EMPLOYER_FEATURES.map((feature, idx) => {
                            const isSelected =
                              formData.features &&
                              formData.features.includes(feature);
                            return (
                              <label
                                key={idx}
                                className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  isSelected
                                    ? "bg-green-50 border-green-300 hover:bg-green-100"
                                    : "bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    togglePredefinedFeature(feature)
                                  }
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                />
                                <span
                                  className={`ml-3 text-sm ${
                                    isSelected
                                      ? "text-green-800 font-medium"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {feature}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* For Job Seeker packages - simple list */}
                {formData.type === PACKAGE_TYPES.JOB_SEEKER &&
                  formData.features &&
                  formData.features.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        Added Features ({formData.features.length}):
                      </p>
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {formData.features.map((feature, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 p-3 rounded-lg mb-2"
                          >
                            <div className="flex items-center">
                              <svg
                                className="w-4 h-4 text-green-600 mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="text-sm text-gray-700">
                                {feature}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFeature(index)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting
                    ? "Saving..."
                    : editingPackage
                    ? "Update Package"
                    : "Create Package"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Packages List - Beautiful Card Design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPackages.map((pkg, index) => (
          <div
            key={pkg.id}
            className={`relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
              pkg.status === PACKAGE_STATUS.ACTIVE
                ? "border-green-500 ring-2 ring-green-100"
                : "border-gray-300"
            }`}
          >
            {/* Status Badge */}
            <div className="absolute -top-4 right-4 z-10">
              <span
                className={`px-4 py-2 rounded-full text-sm font-bold shadow-lg ${
                  pkg.status === PACKAGE_STATUS.ACTIVE
                    ? "bg-green-500 text-white"
                    : "bg-gray-500 text-white"
                }`}
              >
                {pkg.status === PACKAGE_STATUS.ACTIVE ? "ACTIVE" : "INACTIVE"}
              </span>
            </div>

            {/* Package Type Badge */}
            <div className="absolute -top-4 left-4 z-10">
              <span
                className={`px-4 py-2 rounded-full text-sm font-bold shadow-lg ${
                  pkg.type === PACKAGE_TYPES.JOB_SEEKER
                    ? "bg-blue-500 text-white"
                    : "bg-purple-500 text-white"
                }`}
              >
                {pkg.type === PACKAGE_TYPES.JOB_SEEKER ? "EMPLOYEE" : "SPEAKER"}
              </span>
            </div>

            <div className="p-8 pt-12">
              {/* Package Header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {pkg.name}
                </h3>
                <div className="mb-4">
                  <span className="text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    ₹{pkg.price}
                  </span>
                  <span className="text-gray-500 text-lg ml-2">
                    /{pkg.duration} days
                  </span>
                </div>
                {/* Description */}
                {pkg.description && (
                  <div className="text-gray-600 leading-relaxed text-center max-w-sm mx-auto">
                    <p className="text-sm">{pkg.description}</p>
                  </div>
                )}
              </div>

              {/* Package Features */}
              <div className="mb-8">
                {/* For employer packages - show only included features */}
                {pkg.type === PACKAGE_TYPES.EMPLOYER &&
                  pkg.features &&
                  pkg.features.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-green-50">
                        <div className="px-3 py-2 bg-green-100">
                          <p className="text-xs font-semibold text-green-800">
                            ✓ Package Features ({pkg.features.length})
                          </p>
                        </div>
                        <div
                          className="p-2 space-y-1"
                          style={{ maxHeight: "400px", overflowY: "auto" }}
                        >
                          {pkg.features.map((feature, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between px-3 py-2 bg-white rounded"
                            >
                              <span className="text-xs text-gray-800">
                                {feature}
                              </span>
                              <svg
                                className="w-4 h-4 text-green-600 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* For job seeker packages - show features normally */}
                {pkg.type === PACKAGE_TYPES.JOB_SEEKER && (
                  <div className="space-y-3">
                    {pkg.maxApplications && (
                      <div className="flex items-center justify-center">
                        <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                          <svg
                            className="w-4 h-4 text-indigo-600 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-indigo-700 font-medium text-sm">
                            {pkg.maxApplications} Job Applications
                          </span>
                        </div>
                      </div>
                    )}
                    {pkg.features &&
                      pkg.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center justify-center"
                        >
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg
                              className="w-4 h-4 text-indigo-600 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">
                              {feature}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => handleEdit(pkg)}
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105"
                >
                  Edit Package
                </button>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleToggleStatus(pkg)}
                    className={`py-2 px-4 rounded-lg font-medium transition-colors ${
                      pkg.status === PACKAGE_STATUS.ACTIVE
                        ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }`}
                  >
                    {pkg.status === PACKAGE_STATUS.ACTIVE
                      ? "Deactivate"
                      : "Activate"}
                  </button>
                  <button
                    onClick={() => handleDelete(pkg.id)}
                    className="py-2 px-4 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPackages.length === 0 && packages.length > 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No packages found
          </h3>
          <p className="text-gray-500">
            No{" "}
            {filterType === PACKAGE_TYPES.JOB_SEEKER
              ? "employee"
              : filterType === PACKAGE_TYPES.EMPLOYER
              ? "speaker"
              : ""}{" "}
            packages found.
          </p>
          <button
            onClick={() => setFilterType("all")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Show All Packages
          </button>
        </div>
      )}

      {packages.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No packages yet
          </h3>
          <p className="text-gray-500">
            Create your first package to get started.
          </p>
        </div>
      )}
    </div>
  );
};

export default PackageManagement;
